/**
 * Etikett-Vorschau (Design-System › ShelfLabel): immer Schwarz auf Weiß, QR links, Preis groß, Code, Titel max. 2 Zeilen.
 * Maße wie im PDF (62 × 29 mm bzw. 48,5 × 25,4 mm), skaliert über `scale`. Feste Farben sind hier erlaubt (Druckvorlage).
 */
import { useMemo } from "react";
import { encode } from "uqr";
import { formatEuro } from "@shared/geld";
import { qrInhalt } from "@shared/code";

const MASSE = {
  "62x29": { breite: 62, hoehe: 29, qr: 21, preis: 7.5, code: 3.4, titel: 2.9, rand: 2 },
  a4: { breite: 48.5, hoehe: 25.4, qr: 18, preis: 6, code: 2.9, titel: 2.9, rand: 1.5 },
} as const;

export function ShelfLabel({ code, preisCent, titel, format = "62x29", scale = 4 }: { code: string; preisCent: number; titel: string; format?: "62x29" | "a4"; scale?: number }) {
  const m = MASSE[format];
  const qr = useMemo(() => encode(qrInhalt(code), { ecc: "M", border: 1 }), [code]);
  const mm = (v: number) => v * scale;
  const modul = mm(m.qr) / qr.size;
  const pfad = useMemo(() => {
    let d = "";
    qr.data.forEach((zeile, y) => zeile.forEach((an, x) => { if (an) d += `M${x} ${y}h1v1h-1z`; }));
    return d;
  }, [qr]);
  const textX = mm(m.rand + m.qr + 1.5);
  const textBreite = mm(m.breite - m.rand * 2 - m.qr - 1.5);
  return (
    <svg width={mm(m.breite)} height={mm(m.hoehe)} viewBox={`0 0 ${mm(m.breite)} ${mm(m.hoehe)}`} role="img" aria-label={`Etikett ${code}, ${formatEuro(preisCent)}, ${titel}`} style={{ background: "#ffffff", border: "1px solid #d6d6d6", borderRadius: 4, display: "block", maxWidth: "100%", height: "auto" }}>
      <rect width="100%" height="100%" fill="#ffffff" />
      <g transform={`translate(${mm(m.rand)} ${mm((m.hoehe - m.qr) / 2)}) scale(${modul})`}>
        <path d={pfad} fill="#000000" />
      </g>
      <text x={textX} y={mm(m.rand + m.preis)} fontFamily="Inter, system-ui, sans-serif" fontSize={mm(m.preis)} fontWeight={700} fill="#000000">
        {formatEuro(preisCent)}
      </text>
      <text x={textX} y={mm(m.rand + m.preis + 1.2 + m.code)} fontFamily="Inter, system-ui, sans-serif" fontSize={mm(m.code)} fill="#000000" style={{ fontVariantNumeric: "tabular-nums" }}>
        {code}
      </text>
      <foreignObject x={textX} y={mm(m.rand + m.preis + 1.2 + m.code + 1)} width={textBreite} height={mm(m.titel * 2.6)}>
        <div style={{ fontFamily: "Inter, system-ui, sans-serif", fontSize: mm(m.titel), lineHeight: 1.25, color: "#000000", overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const }}>{titel}</div>
      </foreignObject>
    </svg>
  );
}
